import { ComponentType, ReactNode } from 'react'
import { Outlet } from 'react-router-dom'

import i18n from 'modules/core/lib/i18n'

import { FinancialWorkspace } from 'modules/finance/components/FinancialWorkspace'
import { BlurTable } from 'modules/finance/components/FinancialWorkspace/components/Table/BlurTable'
import { UpsellChart } from 'modules/finance/components/FinancialWorkspace/components/chart/UpsellChart'
import {
  InsightTableGroupBy,
  InsightTableType,
} from 'modules/finance/components/FinancialWorkspace/types/table'
import { AvailabilitiesProvider } from 'modules/finance/components/FinancialWorkspaceEmployeeCosts/Providers/Availabilities'
import { CustomFiltersProvider } from 'modules/finance/components/FinancialWorkspaceEmployeeCosts/Providers/CustomFilters'
import { PermissionsProvider } from 'modules/finance/components/FinancialWorkspaceEmployeeCosts/Providers/PermissionsProvider'
import { useFilters } from 'modules/finance/components/FinancialWorkspacePayroll/components/Filters/useFilters'
import { PayrollChart } from 'modules/finance/components/FinancialWorkspacePayroll/components/chart'
import { ResponsivePage } from 'modules/layout/components/ResponsivePage'
import { payrollConceptCategoriesOptions } from 'modules/payroll_bundle/lib/constants'

import { CompensationsBanner } from './components/CompensationsBanner'
import { Header } from './components/Header'
import { useTableProps } from './components/Table/props'

const FinancialWorkspaceCompensations = ({
  authorized,
  seePayrollLanding,
  canSeeFinanceWorkspaceCompensations,
}: {
  authorized: boolean
  seePayrollLanding: boolean
  canSeeFinanceWorkspaceCompensations: boolean
}) => {
  const tableProps = useTableProps()
  const showUpsell = !canSeeFinanceWorkspaceCompensations || seePayrollLanding

  const CustomProvider: ComponentType<{ children: ReactNode }> = ({ children }) => (
    <PermissionsProvider>
      <AvailabilitiesProvider>
        <CustomFiltersProvider>{children}</CustomFiltersProvider>
      </AvailabilitiesProvider>
    </PermissionsProvider>
  )

  return (
    <ResponsivePage.Body authorized={authorized} showHeader={false} variant="full">
      <FinancialWorkspace
        tableProps={tableProps}
        chart={
          showUpsell ? (
            <UpsellChart
              cta={<CompensationsBanner />}
              title={i18n.t('finance.financial_workspace.payroll.total_company_costs')}
            />
          ) : (
            <PayrollChart />
          )
        }
        dataProvider={CustomProvider}
        useAdditionalFilters={useFilters}
        insightTableType={InsightTableType.Compensation}
        defaultGroupBy={InsightTableGroupBy.LineItem}
        header={!showUpsell && <Header />}
        blurTable={
          showUpsell && (
            <BlurTable
              items={payrollConceptCategoriesOptions().map((option) => option.label)}
              rowChildren={<CompensationsBanner />}
            />
          )
        }
      />

      <Outlet context={{ canSeeFinanceWorkspace: authorized }} />
    </ResponsivePage.Body>
  )
}

export default FinancialWorkspaceCompensations
